import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFixtureEntryStatsByLine, fetchQuotesByFixture } from '../../api';
import { playerClubFromQuote } from './gameViewUtils';

function n(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function splitMatchup(label) {
  const parts = String(label || '').split(/\s+v(?:s\.?)?\s+/i);
  return parts.length >= 2 ? [parts[0], parts.slice(1).join(' v ')] : [null, null];
}

function abbr(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';
}

function fmtKickoff(ts) {
  if (!Number.isFinite(Number(ts))) return '—';
  return new Date(Number(ts) * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function marketLabel(id) {
  return String(id || 'Market')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function playerIdFromQuote(q) {
  return q.player_id || String(q.player_name || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

function gateStatus(q) {
  if (q.manual_suspend) return 'suspended';
  if (!q.gate_allowed) return 'gated';
  if (q.manual_shaping_active) return 'limited';
  return 'open';
}

function lineClose(a, b, eps = 1e-3) {
  return Math.abs(n(a, NaN) - n(b, NaN)) < eps;
}

export function aggregateEntryStatsByLine(rows) {
  const byLine = new Map();
  for (const row of rows || []) {
    if (row?.line == null || !Number.isFinite(Number(row.line))) continue;
    const line = Number(row.line);
    let key = line;
    for (const existing of byLine.keys()) {
      if (lineClose(existing, line)) {
        key = existing;
        break;
      }
    }
    const current = byLine.get(key) || {
      line,
      fee_over: 0,
      fee_under: 0,
      liability_net_over: 0,
      liability_net_under: 0,
      n_entries: 0,
      n_entries_over: 0,
      n_entries_under: 0,
      n_slips_over: 0,
      n_slips_under: 0,
      slip_over_num: 0,
      slip_under_num: 0,
      sharp_num: 0,
      sharp_den: 0,
      sharp_over_num: 0,
      sharp_over_den: 0,
      sharp_under_num: 0,
      sharp_under_den: 0,
      p_num: 0,
      p_den: 0,
    };
    const entries = n(row.n_entries);
    const overEntries = n(row.n_entries_over);
    const underEntries = n(row.n_entries_under);
    const overSlips = n(row.n_slips_over);
    const underSlips = n(row.n_slips_under);
    current.fee_over += n(row.total_fees_over);
    current.fee_under += n(row.total_fees_under);
    current.liability_net_over += n(row.liability_net_over);
    current.liability_net_under += n(row.liability_net_under);
    current.n_entries += entries;
    current.n_entries_over += overEntries;
    current.n_entries_under += underEntries;
    current.n_slips_over += overSlips;
    current.n_slips_under += underSlips;
    if (row.avg_slip_size_over != null && overSlips > 0) {
      current.slip_over_num += n(row.avg_slip_size_over) * overSlips;
    }
    if (row.avg_slip_size_under != null && underSlips > 0) {
      current.slip_under_num += n(row.avg_slip_size_under) * underSlips;
    }
    if (row.avg_sharp_score != null && entries > 0) {
      current.sharp_num += n(row.avg_sharp_score) * entries;
      current.sharp_den += entries;
    }
    if (row.avg_sharp_score_over != null && overEntries > 0) {
      current.sharp_over_num += n(row.avg_sharp_score_over) * overEntries;
      current.sharp_over_den += overEntries;
    }
    if (row.avg_sharp_score_under != null && underEntries > 0) {
      current.sharp_under_num += n(row.avg_sharp_score_under) * underEntries;
      current.sharp_under_den += underEntries;
    }
    if (row.true_p_over_pct != null && entries > 0) {
      current.p_num += (n(row.true_p_over_pct) / 100) * entries;
      current.p_den += entries;
    }
    byLine.set(key, current);
  }
  return byLine;
}

/** Latest fair ladder from quote ``alt_overs`` (+ main line) — no history ladder fetch. */
export function ladderRowsFromQuote(q) {
  const byLine = new Map();
  const put = (line, pOver, { overwrite = true } = {}) => {
    if (!Number.isFinite(line)) return;
    let key = line;
    for (const existing of byLine.keys()) {
      if (lineClose(existing, line)) {
        key = existing;
        break;
      }
    }
    if (!overwrite && byLine.has(key)) return;
    byLine.set(key, {
      line: key,
      p_over: pOver == null || !Number.isFinite(Number(pOver)) ? null : Number(pOver),
    });
  };
  put(n(q?.line, 0.5), q?.p_fair, { overwrite: true });
  for (const alt of q?.alt_overs || []) {
    put(n(alt?.line, NaN), alt?.p_fair, { overwrite: false });
  }
  return [...byLine.values()].sort((a, b) => n(a.line) - n(b.line));
}

function lineRowsFromStats(q, statRows, ladderRows) {
  const statsByLine = aggregateEntryStatsByLine(statRows);
  const stats = q.entry_stats || {};
  const ladder = ladderRows?.length ? ladderRows : ladderRowsFromQuote(q);
  const baseLines = ladder.length ? ladder : [{ line: n(q.line, 0.5), p_over: q.p_fair }];
  const rows = baseLines.map((ladderRow) => {
    const line = n(ladderRow.line, n(q.line, 0.5));
    let lineStats = null;
    for (const [statsLine, value] of statsByLine.entries()) {
      if (lineClose(statsLine, line)) {
        lineStats = value;
        break;
      }
    }
    return {
      line,
      fee_over: n(lineStats?.fee_over),
      fee_under: n(lineStats?.fee_under),
      liability_net_over: n(lineStats?.liability_net_over),
      liability_net_under: n(lineStats?.liability_net_under),
      n_entries: n(lineStats?.n_entries),
      n_entries_over: n(lineStats?.n_entries_over),
      n_entries_under: n(lineStats?.n_entries_under),
      n_slips_over: n(lineStats?.n_slips_over),
      n_slips_under: n(lineStats?.n_slips_under),
      avg_slip_over: lineStats?.n_slips_over > 0
        ? lineStats.slip_over_num / lineStats.n_slips_over
        : null,
      avg_slip_under: lineStats?.n_slips_under > 0
        ? lineStats.slip_under_num / lineStats.n_slips_under
        : null,
      avg_sharp: lineStats?.sharp_den > 0 ? lineStats.sharp_num / lineStats.sharp_den : null,
      avg_sharp_over: lineStats?.sharp_over_den > 0
        ? lineStats.sharp_over_num / lineStats.sharp_over_den
        : null,
      avg_sharp_under: lineStats?.sharp_under_den > 0
        ? lineStats.sharp_under_num / lineStats.sharp_under_den
        : null,
      p_fair: ladderRow.p_over == null ? null : n(ladderRow.p_over),
      quote: lineClose(line, q.line),
      gate: gateStatus(q),
      combo: 0,
      market_key: q.market_key,
    };
  });

  for (const lineStats of statsByLine.values()) {
    if (rows.some((row) => lineClose(row.line, lineStats.line))) continue;
    rows.push({
      line: n(lineStats.line, n(q.line, 0.5)),
      fee_over: lineStats.fee_over,
      fee_under: lineStats.fee_under,
      liability_net_over: lineStats.liability_net_over,
      liability_net_under: lineStats.liability_net_under,
      n_entries: lineStats.n_entries,
      n_entries_over: lineStats.n_entries_over,
      n_entries_under: lineStats.n_entries_under,
      n_slips_over: lineStats.n_slips_over,
      n_slips_under: lineStats.n_slips_under,
      avg_slip_over: lineStats.n_slips_over > 0
        ? lineStats.slip_over_num / lineStats.n_slips_over
        : null,
      avg_slip_under: lineStats.n_slips_under > 0
        ? lineStats.slip_under_num / lineStats.n_slips_under
        : null,
      avg_sharp: lineStats.sharp_den > 0 ? lineStats.sharp_num / lineStats.sharp_den : null,
      avg_sharp_over: lineStats.sharp_over_den > 0
        ? lineStats.sharp_over_num / lineStats.sharp_over_den
        : null,
      avg_sharp_under: lineStats.sharp_under_den > 0
        ? lineStats.sharp_under_num / lineStats.sharp_under_den
        : null,
      p_fair: lineStats.p_den > 0 ? lineStats.p_num / lineStats.p_den : null,
      quote: lineClose(lineStats.line, q.line),
      gate: gateStatus(q),
      combo: 0,
      market_key: q.market_key,
    });
  }

  if (rows.some((row) => lineClose(row.line, q.line))) {
    return rows.sort((a, b) => n(a.line) - n(b.line));
  }

  rows.push({
    line: n(q.line, 0.5),
    fee_over: n(stats.total_fees_over),
    fee_under: n(stats.total_fees_under),
    liability_net_over: n(stats.liability_net_over),
    liability_net_under: n(stats.liability_net_under),
    n_entries: n(stats.n_entries),
    n_entries_over: n(stats.n_entries_over),
    n_entries_under: n(stats.n_entries_under),
    n_slips_over: n(stats.n_slips_over),
    n_slips_under: n(stats.n_slips_under),
    avg_slip_over: stats.avg_slip_size_over == null ? null : n(stats.avg_slip_size_over),
    avg_slip_under: stats.avg_slip_size_under == null ? null : n(stats.avg_slip_size_under),
    avg_sharp: stats.avg_sharp_score == null ? null : n(stats.avg_sharp_score),
    avg_sharp_over: stats.avg_sharp_score_over == null ? null : n(stats.avg_sharp_score_over),
    avg_sharp_under: stats.avg_sharp_score_under == null ? null : n(stats.avg_sharp_score_under),
    p_fair: q.p_fair,
    quote: true,
    gate: gateStatus(q),
    combo: 0,
    market_key: q.market_key,
  });

  return rows.sort((a, b) => n(a.line) - n(b.line));
}

function buildPlayers(quotes, statsByMarketKey) {
  const players = new Map();
  for (const q of quotes) {
    const playerId = playerIdFromQuote(q);
    if (!players.has(playerId)) {
      players.set(playerId, {
        id: playerId,
        name: q.player_name || playerId,
        pos: '—',
        team: playerClubFromQuote(q),
        markets: [],
      });
    }
    const player = players.get(playerId);
    let market = player.markets.find((m) => m.id === q.market_id);
    if (!market) {
      market = { id: q.market_id || 'market', name: marketLabel(q.market_id), lines: [] };
      player.markets.push(market);
    }
    market.lines.push(
      ...lineRowsFromStats(
        q,
        statsByMarketKey.get(q.market_key) || [],
        ladderRowsFromQuote(q)
      )
    );
  }
  return [...players.values()].map((p) => ({
    ...p,
    markets: p.markets.map((m) => ({
      ...m,
      lines: [...m.lines].sort((a, b) => n(a.line) - n(b.line)),
    })),
  }));
}

function buildGame(fixtureId, quotes) {
  const first = quotes[0] || {};
  const [homeName, awayName] = splitMatchup(first.team_name);
  const home = homeName || 'Home';
  const away = awayName || 'Away';
  return {
    fixtureId,
    league: first.league || '—',
    sport: first.sport || '—',
    home: { abbr: abbr(home), name: home, color: '#1E3A8A', record: '' },
    away: { abbr: abbr(away), name: away, color: '#3B82F6', record: '' },
    kickoff: fmtKickoff(first.fixture_start_time),
    live: { minute: 'Live', score: { home: '—', away: '—' }, period: first.fixture_status || 'in play' },
  };
}

function buildMainline() {
  return {
    source: 'API quotes',
    lastTick: '—',
    groups: [],
  };
}

function buildGameViewPayload(fixtureId, quotes, entriesPayload) {
  const sortedQuotes = [...(quotes || [])].sort(
    (a, b) =>
      String(a.player_name || '').localeCompare(String(b.player_name || '')) ||
      String(a.market_id || '').localeCompare(String(b.market_id || '')) ||
      n(a.line) - n(b.line)
  );
  const markets = entriesPayload?.markets || {};
  const statsByMarketKey = new Map(
    Object.entries(markets).map(([mk, payload]) => [mk, payload?.lines || []])
  );
  return {
    game: buildGame(fixtureId, sortedQuotes),
    mainline: buildMainline(),
    players: buildPlayers(sortedQuotes, statsByMarketKey),
    combos: [],
  };
}

export const gameViewKeys = {
  detail: (fixtureId) => ['gameView', fixtureId || ''],
  quotes: (fixtureId) => ['gameView', fixtureId || '', 'quotes'],
  entriesByLine: (fixtureId) => ['gameView', fixtureId || '', 'entriesByLine'],
};

export function useGameView(fixtureId, options = {}) {
  const quotesQuery = useQuery({
    queryKey: gameViewKeys.quotes(fixtureId),
    queryFn: () => fetchQuotesByFixture(fixtureId, { includeAlts: true }),
    enabled: !!fixtureId,
    staleTime: 5_000,
    refetchInterval: 15_000,
    ...options,
  });

  const entriesQuery = useQuery({
    queryKey: gameViewKeys.entriesByLine(fixtureId),
    queryFn: () => fetchFixtureEntryStatsByLine(fixtureId),
    enabled: !!fixtureId && quotesQuery.isSuccess,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const data = useMemo(() => {
    if (!fixtureId || !quotesQuery.data) return undefined;
    return buildGameViewPayload(fixtureId, quotesQuery.data, entriesQuery.data);
  }, [fixtureId, quotesQuery.data, entriesQuery.data]);

  return {
    data,
    isLoading: quotesQuery.isLoading,
    isFetching: quotesQuery.isFetching || entriesQuery.isFetching,
    isError: quotesQuery.isError,
    error: quotesQuery.error,
    entriesLoading: entriesQuery.isLoading,
    entriesError: entriesQuery.error,
    refetch: async () => {
      await Promise.all([quotesQuery.refetch(), entriesQuery.refetch()]);
    },
  };
}
